import { addMonths } from 'date-fns';

import { getEnvRequired } from '../../shared/utilities/env';
import { ApiClient, NotFoundError } from '../shared/api/client';

import {
    AgentWorkload,
    AgencyCapacityFillingStatus,
    GetAgencyCapacityFillingStatusApiResponse,
    GetAgentWorkloadApiResponse,
} from './types';

const AGENCY_MANAGER_API_URL = getEnvRequired('AGENCY_MANAGER_API_URL');
const AGENCY_NETWORK_MANAGEMENT_API_URL = getEnvRequired('AGENCY_NETWORK_MANAGEMENT_API_URL');

const apiClient = new ApiClient(false);

/**
 * Fetches the agent away status
 * @returns boolean indicating if agent is away
 */
export async function getAgentAwayStatus(): Promise<boolean> {
    const url = `${AGENCY_MANAGER_API_URL}/agent/away`;
    const response = await apiClient.get<boolean>(url).catch(() => null);
    return response ?? false;
}

/**
 * Sets the agent away status
 * @param isAgentAway - true to set away, false to unset
 */
export async function setAgentAwayStatus(isAgentAway: boolean): Promise<void> {
    const url = `${AGENCY_MANAGER_API_URL}/agent/away`;
    if (isAgentAway) {
        await apiClient.put(url);
    } else {
        await apiClient.delete(url);
    }
}

/**
 * Fetches the agent workload with transformation
 * @returns AgentWorkload with transformed data, or null if workload has never been set (404)
 * @throws {HttpError} For non-404 errors (500, etc.)
 */
export async function fetchAgentWorkload(): Promise<AgentWorkload> {
    const url = `${AGENCY_NETWORK_MANAGEMENT_API_URL}/agent/workload`;

    try {
        const response = await apiClient.get<GetAgentWorkloadApiResponse>(url);
        return {
            status: response.workloadStatus,
            overloadedReason: response.overloadedReason,
            lastUpdatedAt: new Date(response.updatedAt),
        };
    } catch (error) {
        if (error instanceof NotFoundError) {
            return null;
        }
        throw error;
    }
}

/**
 * Sets the agent workload status
 * @param workload - The workload data to set
 */
export async function setAgentWorkload(workload: NonNullable<AgentWorkload>): Promise<void> {
    const url = `${AGENCY_NETWORK_MANAGEMENT_API_URL}/agent/workload`;
    const body = JSON.stringify({
        workloadStatus: workload.status.toString(),
        overloadedReason: workload.overloadedReason?.toString(),
    });
    const headers = {
        'Content-Type': 'application/json',
    };
    await apiClient.post(url, body, headers);
}

/**
 * Fetches the agency capacity filling status for the next two months
 * @returns AgencyCapacityFillingStatus with combined data for next two months
 */
export async function fetchAgencyCapacityFillingStatus(): Promise<AgencyCapacityFillingStatus> {
    const currentDate = new Date();
    const nextMonth = addMonths(currentDate, 1);
    const secondNextMonth = addMonths(currentDate, 2);

    const url = `${AGENCY_NETWORK_MANAGEMENT_API_URL}/agencies/estimable-capacities/overview`;

    const [nextMonthResponse, secondNextMonthResponse] = await Promise.all([
        apiClient.get<GetAgencyCapacityFillingStatusApiResponse>(
            `${url}?year=${nextMonth.getFullYear()}&month=${nextMonth.getMonth() + 1}`,
        ),
        apiClient.get<GetAgencyCapacityFillingStatusApiResponse>(
            `${url}?year=${secondNextMonth.getFullYear()}&month=${secondNextMonth.getMonth() + 1}`,
        ),
    ]);

    return {
        nextMonthIsFilled: nextMonthResponse.partial || nextMonthResponse.complete,
        secondNextMonthIsFilled:
            secondNextMonthResponse.partial || secondNextMonthResponse.complete,
    };
}
